import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import api from "../../lib/api";

/**
 * CreateTemplateDialog — Phase 5 lightweight authoring surface.
 *
 * Designed for the common case (Standard Franchise / Grand Opening etc):
 * pick which modules the template enables, optionally lock them as
 * Brand Standard. Advanced authoring (scheduled changes, multi-version
 * editing) is intentionally deferred — this is the v1 surface that a
 * non-technical operator can grasp in one breath.
 */
export const CreateTemplateDialog = ({ onClose, onSuccess }) => {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || "en").split("-")[0];
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [registry, setRegistry] = useState([]);
    const [moduleSettings, setModuleSettings] = useState({}); // { name: { enabled, lock } }
    const [saving, setSaving] = useState(false);

    // Load the registry from the capability map (so we only show known modules).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get("/operational-base/capability-map");
                if (cancelled) return;
                const list = res.data?.module_registry || [];
                setRegistry(list);
                const init = {};
                for (const m of list) init[m.module_name] = { enabled: true, lock: false };
                setModuleSettings(init);
            } catch (_) {
                // non-fatal
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const toggleModule = (name, field) => {
        setModuleSettings((s) => ({
            ...s,
            [name]: { ...s[name], [field]: !s[name][field] },
        }));
    };

    const handleSave = async () => {
        if (!name.trim()) {
            toast.error(t("ob.templates.create_name_required"));
            return;
        }
        const modules = Object.entries(moduleSettings)
            .filter(([_, v]) => v.enabled || v.lock)
            .map(([n, v]) => ({
                module_name: n,
                enabled: !!v.enabled,
                lock_provenance: v.lock ? "operational" : null,
                break_glass_eligible: true,
                lock_reason: v.lock ? `Set by template "${name.trim()}"` : null,
            }));
        if (modules.length === 0) {
            toast.error(t("ob.templates.create_empty_modules"));
            return;
        }
        setSaving(true);
        try {
            await api.post("/operational-base/templates", {
                name: name.trim(),
                description: description.trim() || null,
                applicable_org_types: ["location", "regional", "corporate"],
                modules,
                change_summary: "Initial baseline",
            });
            toast.success(t("ob.templates.create_success"));
            onSuccess && onSuccess();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("ob.templates.create_error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            data-testid="ob-template-create-dialog"
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.6)" }}
            onClick={onClose}
        >
            <div
                role="document"
                className="ob-card w-full max-w-xl max-h-[90vh] overflow-y-auto"
                style={{ background: "var(--ob-surface-elevated)", padding: "2rem" }}
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-start justify-between mb-5">
                    <div>
                        <h2 className="text-xl" style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}>
                            {t("ob.templates.create_title")}
                        </h2>
                        <p className="text-sm mt-1" style={{ color: "var(--ob-text-secondary)" }}>
                            {t("ob.templates.create_subtitle")}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t("common.close")}
                        className="p-1 rounded hover:bg-white/5"
                        style={{ color: "var(--ob-text-tertiary)" }}
                        data-testid="ob-template-create-close"
                    >
                        <X size={18} strokeWidth={1.5} aria-hidden="true" />
                    </button>
                </header>

                <div className="space-y-4 mb-5">
                    <div>
                        <label className="ob-eyebrow block mb-1.5">{t("ob.templates.name_label")}</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t("ob.templates.name_placeholder")}
                            data-testid="ob-template-name-input"
                            className="w-full rounded-xl px-3 py-2.5 text-sm"
                            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--ob-text-primary)" }}
                        />
                    </div>
                    <div>
                        <label className="ob-eyebrow block mb-1.5">{t("ob.templates.description_label")}</label>
                        <input
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={t("ob.templates.description_placeholder")}
                            data-testid="ob-template-description-input"
                            className="w-full rounded-xl px-3 py-2.5 text-sm"
                            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--ob-text-primary)" }}
                        />
                    </div>
                </div>

                <div className="mb-5">
                    <p className="ob-eyebrow mb-2">{t("ob.templates.modules_label")}</p>
                    <div className="space-y-2">
                        {registry.map((m) => {
                            const setting = moduleSettings[m.module_name] || { enabled: false, lock: false };
                            const dn = m.display_name?.[lang] || m.display_name?.en || m.module_name;
                            return (
                                <div
                                    key={m.module_name}
                                    data-testid={`ob-template-module-${m.module_name}`}
                                    className="flex items-center justify-between rounded-xl px-3 py-2.5"
                                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
                                >
                                    <span className="text-sm" style={{ color: "var(--ob-text-primary)" }}>{dn}</span>
                                    <div className="flex items-center gap-3 text-xs">
                                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={setting.enabled}
                                                onChange={() => toggleModule(m.module_name, "enabled")}
                                                data-testid={`ob-template-module-enabled-${m.module_name}`}
                                            />
                                            <span style={{ color: "var(--ob-text-secondary)" }}>{t("ob.templates.enable_label")}</span>
                                        </label>
                                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={setting.lock}
                                                onChange={() => toggleModule(m.module_name, "lock")}
                                                disabled={!setting.enabled}
                                                data-testid={`ob-template-module-lock-${m.module_name}`}
                                            />
                                            <span style={{ color: "var(--ob-text-secondary)", opacity: setting.enabled ? 1 : 0.5 }}>
                                                {t("ob.templates.lock_label")}
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        data-testid="ob-template-create-cancel"
                        className="text-sm px-4 py-2 rounded-full"
                        style={{ color: "var(--ob-text-secondary)" }}
                    >
                        {t("common.cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="ob-template-create-save"
                        className="inline-flex items-center gap-1.5 text-sm rounded-full px-5 py-2.5"
                        style={{ background: "#10B981", color: "#0B0F12", opacity: saving ? 0.5 : 1 }}
                    >
                        <Plus size={14} strokeWidth={1.5} aria-hidden="true" />
                        {saving ? t("ob.templates.creating") : t("ob.templates.save_btn")}
                    </button>
                </div>
            </div>
        </div>
    );
};
